/*
 * Motif combobox d'ARIA 1.2 : `role="combobox"` sur l'input, `role="listbox"`
 * sur le conteneur des propositions, `role="option"` sur chacune. Deux règles
 * jsx-a11y désapprouvent ces rôles et proposent `select`/`datalist` à la
 * place ; aucun des deux ne convient à une liste asynchrone dont le focus reste
 * dans le champ de saisie.
 *
 * Dans ce motif, le clavier est géré sur l'input (flèches, Entrée, Échap) via
 * `aria-activedescendant` : les options ne reçoivent jamais le focus, donc
 * exiger un écouteur clavier sur chacune n'aurait pas de sens.
 */
// oxlint-disable jsx-a11y/prefer-tag-over-role
// oxlint-disable jsx-a11y/click-events-have-key-events
import type { PlaceDto } from "@boomap/contracts";
import { useId, useRef, useState } from "react";

import { useI18n } from "../i18n/index.ts";
import { MIN_QUERY_LENGTH, usePlaceSearch } from "../lib/use-place-search.ts";

export interface SearchBoxProps {
  readonly onSelect: (place: PlaceDto) => void;
}

/**
 * Champ de recherche de lieu (ville, quartier, rue, adresse).
 *
 * Le motif ARIA est celui d'une combobox : le rôle est porté par l'`input`
 * lui-même, comme le prévoit ARIA 1.2, et `aria-activedescendant` déplace le
 * focus virtuel dans la liste sans jamais quitter le champ.
 */
export const SearchBox = ({ onSelect }: SearchBoxProps): React.ReactElement => {
  const { t } = useI18n();
  const search = usePlaceSearch();
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listId = useId();
  const optionId = (index: number): string => `${listId}-option-${index.toString()}`;

  const hasResults = search.results.length > 0;
  const showEmpty = search.searched && !hasResults && !search.loading;
  const open = hasResults || showEmpty || search.error !== null;

  const choose = (place: PlaceDto): void => {
    onSelect(place);
    search.settle(place.label);
    setActiveIndex(-1);
    inputRef.current?.blur();
  };

  const move = (delta: number): void => {
    if (!hasResults) {
      return;
    }

    const count = search.results.length;

    setActiveIndex((current) => {
      const next = current + delta;

      if (next < 0) {
        return count - 1;
      }

      return next >= count ? 0 : next;
    });
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    switch (event.key) {
      case "ArrowDown": {
        event.preventDefault();
        move(1);
        break;
      }
      case "ArrowUp": {
        event.preventDefault();
        move(-1);
        break;
      }
      case "Enter": {
        const place = search.results[activeIndex] ?? search.results[0];

        if (place !== undefined) {
          event.preventDefault();
          choose(place);
        }

        break;
      }
      case "Escape": {
        search.reset();
        setActiveIndex(-1);
        break;
      }
      default: {
        break;
      }
    }
  };

  return (
    <div className="search">
      <svg className="search__glyph" viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
        <circle cx="8.5" cy="8.5" r="5.6" fill="none" stroke="currentColor" strokeWidth="1.9" />
        <path
          d="M12.9 12.9 L17.2 17.2"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>

      <input
        ref={inputRef}
        className="search__input"
        type="text"
        role="combobox"
        value={search.query}
        placeholder={t.search.placeholder}
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        aria-label={t.search.label}
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
        onChange={(event) => {
          search.setQuery(event.target.value);
          setActiveIndex(-1);
        }}
        onKeyDown={onKeyDown}
      />

      {search.query.length > 0 && (
        <button
          type="button"
          className="search__clear"
          aria-label={t.search.clear}
          onClick={() => {
            search.setQuery("");
            search.reset();
            setActiveIndex(-1);
            inputRef.current?.focus();
          }}
        >
          ✕
        </button>
      )}

      <div className="search__results" id={listId} role="listbox" hidden={!open}>
        {search.error !== null && (
          <p className="search__message search__message--error">{search.error}</p>
        )}

        {showEmpty && search.error === null && <p className="search__message">{t.search.empty}</p>}

        {search.results.map((place, index) => (
          <div
            key={`${place.label}-${place.latitude}-${place.longitude}`}
            id={optionId(index)}
            role="option"
            tabIndex={-1}
            aria-selected={index === activeIndex}
            className={`search__result ${index === activeIndex ? "search__result--active" : ""}`}
            onMouseEnter={() => setActiveIndex(index)}
            onClick={() => choose(place)}
          >
            <span className="search__label">{place.label}</span>
            {place.context !== null && <span className="search__context">{place.context}</span>}
          </div>
        ))}
      </div>

      {search.query.trim().length > 0 && search.query.trim().length < MIN_QUERY_LENGTH && (
        <p className="search__hint">{t.search.tooShort}</p>
      )}
    </div>
  );
};
